import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  Calendar,
  CalendarDays,
  ClipboardList,
  Megaphone,
  FileText,
  User,
  ShieldCheck,
  LogOut,
  Menu,
  X,
  Inbox,
  Bell,
  Headset,
} from 'lucide-react';
import { shiftReplacementAPI, notificationAPI } from '../lib/api';

const NAV = [
  { path: '/employee',               icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/employee/schedule',      icon: Calendar,        label: 'My Schedule' },
  { path: '/employee/timesheets',    icon: ClipboardList,   label: 'My Timesheets' },
  { path: '/availability',           icon: CalendarDays,    label: 'My Availability' },
  { path: '/shift-replacements',     icon: Inbox,           label: 'Replacements', badgeKey: 'replacement_unread' },
  { path: '/employee/announcements', icon: Megaphone,       label: 'Announcements' },
  { path: '/employee/notifications', icon: Bell,            label: 'Notifications', badgeKey: 'notifications_unread' },
  { path: '/employee/documents',     icon: FileText,        label: 'Documents' },
  { path: '/support',                icon: Headset,         label: 'Workplace Support', badgeKey: 'support_unread' },
  { path: '/employee/profile',       icon: User,            label: 'My Profile' },
  { path: '/employee/profile',       icon: ShieldCheck,     label: 'Account Security', testId: 'account-security' },
];

export function EmployeeLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [badges, setBadges] = useState({});

  // Replacement inbox unread count — single poll, 60s interval
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await shiftReplacementAPI.unreadCount();
        if (!cancelled) setBadges((b) => ({ ...b, replacement_unread: r.data?.unread || 0 }));
      } catch { /* silent */ }
      try {
        const n = await notificationAPI.getAll();
        const all = Array.isArray(n.data) ? n.data : [];
        const unread = all.filter(x => !x.read).length;
        const supportUnread = all.filter(x => !x.read && x.type === 'support_reply').length;
        if (!cancelled) setBadges((b) => ({
          ...b,
          notifications_unread: unread,
          support_unread: supportUnread,
        }));
      } catch { /* silent */ }
    };
    tick();
    const t = setInterval(tick, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const initials = (user?.name || 'E')
    .split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  const SidebarContent = ({ onLinkClick }) => (
    <>
      {/* User info */}
      <div className="px-4 py-3 border-b border-slate-700">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 text-white text-xs font-bold">
            {initials}
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-medium truncate">{user?.name || 'Employee'}</p>
            <p className="text-slate-400 text-xs truncate">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Nav items */}
      <nav className="flex-1 py-3 space-y-0.5 px-2 overflow-y-auto">
        {NAV.map(({ path, icon: Icon, label, testId, badgeKey }) => {
          const active = location.pathname === path;
          const badge = badgeKey ? badges[badgeKey] : 0;
          return (
            <Link
              key={testId || label}
              to={path}
              data-testid={`employee-nav-${testId || label.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={onLinkClick}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                active
                  ? 'bg-orange-500 text-white'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <Icon size={16} className="flex-shrink-0" />
              <span className="flex-1">{label}</span>
              {badge > 0 && (
                <span
                  className="min-w-[18px] h-[18px] px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center"
                  data-testid={`sidebar-badge-${badgeKey}`}
                >
                  {badge > 9 ? '9+' : badge}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="px-2 pb-4 flex-shrink-0">
        <button
          onClick={() => { if (onLinkClick) onLinkClick(); handleLogout(); }}
          data-testid="employee-logout-btn"
          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:bg-slate-800 hover:text-red-400 transition-colors"
        >
          <LogOut size={16} />
          Sign Out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">

      {/* ── Desktop Sidebar (md+) ── */}
      <aside className="hidden md:flex w-56 bg-slate-900 flex-col flex-shrink-0">
        {/* Brand */}
        <div className="flex items-center gap-2.5 px-5 py-5 border-b border-slate-700 flex-shrink-0">
          <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-sm">RS</span>
          </div>
          <span className="font-bold text-white text-base" style={{ fontFamily: 'Manrope' }}>
            ReStaff
          </span>
        </div>
        <SidebarContent onLinkClick={null} />
      </aside>

      {/* ── Mobile Header ── */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-14 bg-slate-900 border-b border-slate-700 flex items-center justify-between px-4 z-50">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-sm">RS</span>
          </div>
          <span className="font-bold text-white text-base" style={{ fontFamily: 'Manrope' }}>
            ReStaff
          </span>
        </div>
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 text-slate-300 hover:text-white rounded-md"
          data-testid="employee-mobile-menu-btn"
        >
          <Menu size={22} />
        </button>
      </header>

      {/* ── Mobile Sidebar Backdrop ── */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 z-40"
          onClick={() => setMobileOpen(false)}
          data-testid="employee-mobile-backdrop"
        />
      )}

      {/* ── Mobile Slide-in Sidebar ── */}
      <div
        className="md:hidden fixed left-0 top-0 bottom-0 w-64 bg-slate-900 z-50 flex flex-col shadow-2xl"
        style={{
          transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.25s ease',
        }}
        data-testid="employee-mobile-sidebar"
      >
        {/* Brand + close */}
        <div className="flex items-center justify-between px-4 border-b border-slate-700 flex-shrink-0" style={{ minHeight: 56 }}>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">RS</span>
            </div>
            <span className="font-bold text-white text-base" style={{ fontFamily: 'Manrope' }}>
              ReStaff
            </span>
          </div>
          <button
            onClick={() => setMobileOpen(false)}
            className="p-2 text-slate-400 hover:text-white rounded-md"
            data-testid="employee-mobile-close-btn"
          >
            <X size={20} />
          </button>
        </div>
        <SidebarContent onLinkClick={() => setMobileOpen(false)} />
      </div>

      {/* ── Main content ── */}
      <main className="flex-1 min-w-0 overflow-x-hidden overflow-y-auto pt-14 md:pt-0">
        {children}
      </main>
    </div>
  );
}
